import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export type CancellationLabelText = 'Urgent' | 'High Priority' | 'Priority Shift' | 'Needs Coverage';

export interface CancellationLabelInfo {
  text: CancellationLabelText;
  color: string;
  updatedAtIso: string;
  msUntilNextChange: number; 
  nextLabelText?: CancellationLabelText; 
  nextChangeAtIso?: string; 
}

/**
 * Compute dynamic cancellation label based on time remaining until shift start.
 * Uses calendar hours and assumes postedStart is the canonical start moment when available.
 * Returns null if inputs are insufficient or shift is in the past.
 */
export function computeCancellationLabel(params: {
  startDate?: string;
  postedStartTime?: string;
  postedStartIso?: string;
  nowIso?: string;
}): CancellationLabelInfo | null {
  const now = params.nowIso ? dayjs(params.nowIso) : dayjs();

  let start: dayjs.Dayjs | null = null;
  if (params.postedStartIso) {
    const s = dayjs(params.postedStartIso);
    start = s.isValid() ? s : null;
  } else if (params.startDate && params.postedStartTime) {
    const candidates = [
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD h:mm a',
    ];
    let parsed: dayjs.Dayjs | null = null;
    for (const fmt of candidates) {
      const s = dayjs(`${params.startDate} ${params.postedStartTime}`, fmt, true);
      if (s.isValid()) { parsed = s; break; }
    }
    start = parsed && parsed.isValid() ? parsed : null;
  }

  if (!start || !start.isValid()) return null;

  const diffHours = start.diff(now, 'hour', true);
  if (diffHours <= 0) return null;

  type Bucket = { text: CancellationLabelText; color: string; lower: number; upper: number | null };
  const buckets: Bucket[] = [
    { text: 'Urgent', color: '#dc3545', lower: 0, upper: 48 },
    { text: 'High Priority', color: '#dc3545', lower: 48, upper: 96 },
    { text: 'Priority Shift', color: '#ffc107', lower: 96, upper: 168 },
    { text: 'Needs Coverage', color: '#007bff', lower: 168, upper: null },
  ];

  let bucket: Bucket | null = null;
  let bucketIndex = -1;
  for (let i = 0; i < buckets.length; i++) {
    const b = buckets[i];
    const inLower = diffHours >= b.lower - Number.EPSILON;
    const inUpper = b.upper == null ? true : diffHours <= b.upper + Number.EPSILON;
    if (inLower && inUpper) { bucket = b; bucketIndex = i; break; }
  }
  if (!bucket) bucket = buckets[buckets.length - 1];
  if (bucketIndex === -1) bucketIndex = buckets.length - 1;

  const nextBoundaryHours: number = bucket.lower;
  const msUntilNextChange = Math.max(0, Math.round((diffHours - nextBoundaryHours) * 3600 * 1000));

  const nextLabelText: CancellationLabelText | undefined = bucketIndex > 0
    ? buckets[bucketIndex - 1].text
    : undefined;
  const nextChangeAtIso = msUntilNextChange > 0 ? now.add(msUntilNextChange, 'millisecond').toISOString() : undefined;

  return {
    text: bucket.text,
    color: bucket.color,
    updatedAtIso: now.toISOString(),
    msUntilNextChange,
    nextLabelText,
    nextChangeAtIso,
  };
}


